import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAppId } from "../../context/AppIdContext";
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const UserWalletList = () => {
    // --- Context & State ---
    const { appId, language } = useAppId();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    
    // Data States
    const [users, setUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");

    // Modal States
    const [showAllocateModal, setShowAllocateModal] = useState(false);
    const [allocateAmount, setAllocateAmount] = useState("");
    const [selectedUser, setSelectedUser] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const lang = language || 'en';

    const t = {
        en: {
            title: "User Wallets",
            subtitle: "Manage and allocate message balances for all users.",
            searchPlaceholder: "Search by name...",
            balance: "Current Balance",
            allocateBtn: "Allocate Balance",
            admin: "Admin",
            subAdmin: "Sub Admin",
            volunteer: "Volunteer",
            boothPramukh: "Booth Pramukh",
            noUsers: "No users found matching",
            loading: "Loading User Wallets...",
            back: "Back to Dashboard"
        },
        hi: {
            title: "उपयोगकर्ता वॉलेट",
            subtitle: "सभी उपयोगकर्ताओं के लिए संदेश शेष प्रबंधित और आवंटित करें।",
            searchPlaceholder: "नाम से खोजें...",
            balance: "वर्तमान शेष",
            allocateBtn: "शेष आवंटित करें",
            admin: "एडमिन",
            subAdmin: "सब एडमिन",
            volunteer: "कार्यकर्ता",
            boothPramukh: "बूथ प्रमुख",
            noUsers: "कोई उपयोगकर्ता नहीं मिला",
            loading: "उपयोगकर्ता वॉलेट लोड हो रहे हैं...",
            back: "डैशबोर्ड पर वापस जाएं"
        },
        gu: {
            title: "વપરાશકર્તા વૉલેટ્સ",
            subtitle: "તમામ વપરાશકર્તાઓ માટે સંદેશ બેલેન્સ મેનેજ કરો અને ફાળવો.",
            searchPlaceholder: "નામ દ્વારા શોધો...",
            balance: "વર્તમાન બેલેન્સ",
            allocateBtn: "બેલેન્સ ફાળવો",
            admin: "એડમિન",
            subAdmin: "સબ એડમિન",
            volunteer: "સ્વયંસેવક",
            boothPramukh: "બૂથ પ્રમુખ",
            noUsers: "કોઈ વપરાશકર્તા મળ્યા નથી",
            loading: "વપરાશકર્તા વૉલેટ લોડ થઈ રહ્યાં છે...",
            back: "ડેશબોર્ડ પર પાછા જાઓ"
        }
    };

    const txt = t[lang] || t['en'];

    const getUserTypeLabel = (type) => {
        const map = {
            'A': txt.admin,
            'sa': txt.subAdmin,
            'k': txt.volunteer,
            'BP': txt.boothPramukh
        };
        return map[type] || type || 'User';
    };

    // --- Fetch Data ---
    useEffect(() => {
        if (appId) {
            fetchUserWallets();
        }
    }, [appId]);

    const fetchUserWallets = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${BASE_URL}/api/dis_wtsp_user_wallets`, {
                app_id: appId
            });

            if (response.data.success && response.data.data && response.data.data.result) {
                setUsers(response.data.data.result);
            } else {
                setUsers([]);
            }
        } catch (error) {
            console.error("Error fetching user wallets:", error);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    // --- Handle Allocation Submission ---
    const handleAllocate = async () => {
        if (!selectedUser || !allocateAmount || allocateAmount <= 0) {
            alert("Please enter a valid amount");
            return;
        }
        setSubmitting(true);
        try {
            const response = await axios.post(`${BASE_URL}/api/allocate-balance`, {
                app_id: appId,
                user_id: selectedUser.user_id,
                amount: allocateAmount
            });
            if (response.data.success) {
                alert("Balance allocated successfully!");
                setShowAllocateModal(false);
                setSelectedUser(null);
                setAllocateAmount("");
                fetchUserWallets(); // Refresh balances
            } else {
                alert(response.data.message || "Allocation failed");
            }
        } catch (error) {
            console.error("Allocation error:", error);
            alert("Error allocating balance");
        } finally {
            setSubmitting(false);
        }
    };

    const openAllocateModal = (user) => {
        setSelectedUser(user);
        setAllocateAmount("");
        setShowAllocateModal(true);
    };

    // --- Filter Logic ---
    const filteredUsers = users.filter(user => {
        if (!searchTerm) return true;
        const lowerSearch = searchTerm.toLowerCase();
        return user.name && user.name.toLowerCase().includes(lowerSearch);
    });

    if (loading) {
        return (
            <div className="loader-wrapper">
                <div className="spinner"></div>
                <p className="loader-text">{txt.loading}</p>
                <style jsx>{`
                    .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 100vh; width: 100%; background: #F4F5F8; }
                    .spinner { width: 50px; height: 50px; border: 4px solid #F4F5F7; border-top: 4px solid #8B5CF6; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; }
                    .loader-text { color: #8E92A3; font-weight: 600; font-size: 1.1rem; }
                    @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                `}</style>
            </div>
        );
    }

    return (
        <div className="wallet-container">
            {/* Allocation Modal */}
            {showAllocateModal && selectedUser && (
                <div className="wallet-modal-overlay" onClick={() => setShowAllocateModal(false)}>
                    <div className="wallet-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="wallet-modal-header">
                            <h3>Allocate to {selectedUser.name}</h3>
                            <button className="wallet-modal-close" onClick={() => setShowAllocateModal(false)}>×</button>
                        </div>
                        <div className="wallet-modal-body">
                            <div className="current-balance-display mb-4">
                                <span className="text-muted">Current Balance:</span>
                                <h4>{selectedUser.total_messages.toLocaleString()} Msgs</h4>
                            </div>
                            <label>Amount to Allocate (Msgs)</label>
                            <input
                                type="number"
                                className="wallet-modal-input"
                                placeholder="Enter amount"
                                value={allocateAmount}
                                onChange={(e) => setAllocateAmount(e.target.value)}
                            />
                        </div>
                        <div className="wallet-modal-footer">
                            <button className="wallet-btn-cancel" onClick={() => setShowAllocateModal(false)}>Cancel</button>
                            <button className="wallet-btn-submit" onClick={handleAllocate} disabled={submitting}>
                                {submitting ? "Processing..." : "Allocate Now"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Back Navigation Header */}
            <div className="dashboard-header-wrapper">
                <Header title={txt.title} className="p-5 m-5"/>
            </div>

            <main className="dashboard-main">
                <div className="page-header slide-up-element">
                    <div>
                        <p className="page-subtitle">{txt.subtitle}</p>
                    </div>
                    {/* Styled Search Bar */}
                    <div className="search-bar-wrapper">
                        <div className="search-bar">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8E92A3" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                            </svg>
                            <input 
                                type="text" 
                                placeholder={txt.searchPlaceholder} 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {/* User Cards Grid */}
                <div className="row g-4 mt-2">
                    {filteredUsers.length > 0 ? (
                        filteredUsers.map((user, index) => {
                            // Fix duplicate HTTP issues in photo_path if present from bad DB entries
                            let safePhotoPath = user.photo_path;
                            if (safePhotoPath && safePhotoPath.includes('http') && safePhotoPath.lastIndexOf('http') > 0) {
                                safePhotoPath = safePhotoPath.substring(safePhotoPath.lastIndexOf('http'));
                            }

                            return (
                                <div className="col-12 col-md-6 col-lg-4 slide-up-element" style={{ animationDelay: `${0.1 * (index % 5)}s` }} key={user.user_id}>
                                    <div className="user-wallet-card">
                                        <div className="card-top">
                                            <div className="user-avatar">
                                                <img 
                                                    src={safePhotoPath || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=F29D12&color=fff`} 
                                                    onError={(e) => {
                                                        e.target.onerror = null; 
                                                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=F29D12&color=fff`;
                                                    }}
                                                    alt={user.name} 
                                                />
                                            </div>
                                            <div className="user-info">
                                                <h3 className="user-name" title={user.name}>{user.name}</h3>
                                                <span className="user-badge">{getUserTypeLabel(user.user_type)}</span>
                                            </div>
                                        </div>

                                        <div className="card-middle">
                                            <div className="balance-label">{txt.balance}</div>
                                            <div className="balance-amount">{user.total_messages.toLocaleString()} <span className="msgs-text">Msgs</span></div>
                                        </div>

                                        <div className="card-bottom">
                                            <button className="allocate-btn" onClick={() => openAllocateModal(user)}>
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                                </svg>
                                                {txt.allocateBtn}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="col-12 text-center" style={{ padding: '60px 20px', color: '#8E92A3' }}>
                            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="1.5" style={{ marginBottom: '16px' }}>
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                            <h4 style={{ fontWeight: 700, color: '#475569' }}>{txt.noUsers} "{searchTerm}"</h4>
                        </div>
                    )}
                </div>
            </main>

            <style jsx>{`
                /* --- KEYFRAMES --- */
                @keyframes slideInUp {
                    from { opacity: 0; transform: translateY(15px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                /* --- GLOBAL --- */
                .wallet-container {
                    background-color: #F4F5F8;
                    min-height: 100vh;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                    width: 100%;
                    box-sizing: border-box;
                }
                
                .wallet-container * { box-sizing: border-box; }

                .slide-up-element {
                    opacity: 0;
                    animation: slideInUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }

                /* --- HEADER & BACK BTN --- */
                .dashboard-header-wrapper {
                    padding: 16px 20px;
                }
                @media (min-width: 768px) {
                    .dashboard-header-wrapper { padding: 25px 25px 0 25px; }
                }

                .back-navigate-btn {
                    display: inline-flex;
                    align-items: center;
                    background: #FFFFFF;
                    border: 1px solid #E2E8F0;
                    padding: 8px 16px;
                    border-radius: 10px;
                    color: #475569;
                    font-weight: 700;
                    font-size: 0.9rem;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
                }
                .back-navigate-btn:hover {
                    background: #F8FAFC;
                    color: #1E293B;
                    border-color: #CBD5E1;
                    transform: translateY(-1px);
                }

                /* --- MAIN CONTENT & SEARCH --- */
                .dashboard-main {
                    padding: 16px 20px;
                    margin: 0 auto;
                }
                @media (min-width: 768px) {
                    .dashboard-main { padding: 24px 32px; }
                }

                .page-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 16px;
                }
                .page-subtitle {
                    font-size: 1.05rem;
                    font-weight: 700;
                    color: #475569;
                    margin: 0;
                }

                .search-bar-wrapper {
                    flex: 1;
                    max-width: 350px;
                }
                @media (max-width: 768px) {
                    .search-bar-wrapper { max-width: 100%; width: 100%; }
                }
                .search-bar {
                    display: flex;
                    align-items: center;
                    background: #FFFFFF;
                    padding: 12px 16px;
                    border-radius: 14px;
                    gap: 10px;
                    border: 1px solid #E2E8F0;
                    box-shadow: 0 4px 6px rgba(0,0,0,0.02);
                    transition: all 0.2s ease;
                }
                .search-bar:focus-within {
                    border-color: #8B5CF6;
                    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
                }
                .search-bar input {
                    border: none;
                    background: transparent;
                    outline: none;
                    font-size: 0.95rem;
                    width: 100%;
                    color: #1E293B;
                }
                .search-bar input::placeholder { color: #94A3B8; }

                /* --- USER WALLET CARDS --- */
                .user-wallet-card {
                    background: #FFFFFF;
                    border-radius: 20px;
                    padding: 24px;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.05);
                    border: 1px solid rgba(226, 232, 240, 0.6);
                    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                }
                .user-wallet-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 15px 35px -10px rgba(0, 0, 0, 0.08);
                    border-color: #E2E8F0;
                }

                .card-top {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    margin-bottom: 20px;
                    padding-bottom: 16px;
                    border-bottom: 1px dashed #E2E8F0;
                }
                .user-avatar {
                    width: 56px;
                    height: 56px;
                    border-radius: 16px;
                    overflow: hidden;
                    background: #F1F5F9;
                    flex-shrink: 0;
                    box-shadow: 0 4px 10px rgba(0,0,0,0.05);
                }
                .user-avatar img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
                .user-info {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    overflow: hidden;
                }
                .user-name {
                    font-size: 1.1rem;
                    font-weight: 800;
                    color: #1E293B;
                    margin: 0;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .user-badge {
                    align-self: flex-start;
                    font-size: 0.7rem;
                    font-weight: 700;
                    color: #8B5CF6;
                    background: #F5F3FF;
                    padding: 4px 10px;
                    border-radius: 8px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .card-middle {
                    margin-bottom: 24px;
                    flex: 1;
                }
                .balance-label {
                    font-size: 0.8rem;
                    font-weight: 600;
                    color: #64748B;
                    margin-bottom: 4px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                .balance-amount {
                    font-size: 2.2rem;
                    font-weight: 800;
                    color: #8B5CF6;
                    line-height: 1;
                    letter-spacing: -0.5px;
                }
                .msgs-text {
                    font-size: 1rem;
                    color: #94A3B8;
                    font-weight: 600;
                }

                .card-bottom {
                    margin-top: auto;
                }
                .allocate-btn {
                    width: 100%;
                    background: linear-gradient(135deg, #8B5CF6 0%, #8B5CF6 100%);
                    color: white;
                    border: none;
                    border-radius: 14px;
                    padding: 14px;
                    font-size: 0.95rem;
                    font-weight: 700;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(242, 157, 18, 0.2);
                }
                .allocate-btn:hover {
                    box-shadow: 0 6px 16px rgba(242, 157, 18, 0.35);
                    transform: translateY(-2px);
                }

                /* --- MODALS --- */
                .wallet-modal-overlay {
                    position: fixed;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(15, 23, 42, 0.6);
                    backdrop-filter: blur(8px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 20px;
                    animation: fadeIn 0.2s ease-out;
                }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                
                .wallet-modal-content {
                    background: #FFFFFF;
                    border-radius: 24px;
                    width: 100%;
                    max-width: 450px;
                    overflow: hidden;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                    animation: scaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                }
                @keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
                
                .wallet-modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 24px 32px;
                    border-bottom: 1px solid #F1F5F9;
                    background: #F8FAFC;
                }
                .wallet-modal-header h3 {
                    margin: 0;
                    font-size: 1.25rem;
                    font-weight: 800;
                    color: #1E293B;
                }
                .wallet-modal-close {
                    background: none;
                    border: none;
                    font-size: 28px;
                    cursor: pointer;
                    color: #94A3B8;
                    transition: color 0.2s;
                }
                .wallet-modal-close:hover { color: #1E293B; }
                
                .wallet-modal-body { padding: 32px; }
                .current-balance-display h4 {
                    font-size: 1.5rem;
                    font-weight: 800;
                    color: #8B5CF6;
                    margin: 4px 0 0 0;
                }
                .wallet-modal-body label {
                    display: block;
                    font-size: 0.85rem;
                    font-weight: 600;
                    margin-bottom: 8px;
                    color: #475569;
                }
                .wallet-modal-input {
                    width: 100%;
                    padding: 14px 16px;
                    border: 2px solid #E2E8F0;
                    border-radius: 12px;
                    font-size: 1rem;
                    outline: none;
                    background: #FFFFFF;
                    transition: all 0.2s;
                    color: #1E293B;
                    font-weight: 600;
                }
                .wallet-modal-input:focus {
                    border-color: #8B5CF6;
                    box-shadow: 0 0 0 4px rgba(242, 157, 18, 0.1);
                }
                .wallet-modal-footer {
                    display: flex;
                    gap: 16px;
                    padding: 24px 32px;
                    border-top: 1px solid #F1F5F9;
                    background: #F8FAFC;
                }
                .wallet-btn-cancel, .wallet-btn-submit {
                    flex: 1;
                    padding: 14px;
                    border-radius: 12px;
                    font-weight: 700;
                    cursor: pointer;
                    font-size: 0.95rem;
                    transition: all 0.2s;
                }
                .wallet-btn-cancel {
                    background: #E2E8F0;
                    border: none;
                    color: #475569;
                }
                .wallet-btn-cancel:hover { background: #CBD5E1; color: #1E293B; }
                .wallet-btn-submit {
                    background: linear-gradient(135deg, #8B5CF6 0%, #8B5CF6 100%);
                    border: none;
                    color: white;
                    box-shadow: 0 4px 6px -1px rgba(242, 157, 18, 0.2);
                }
                .wallet-btn-submit:hover:not(:disabled) {
                    transform: translateY(-1px);
                    box-shadow: 0 6px 10px -1px rgba(242, 157, 18, 0.3);
                }
                .wallet-btn-submit:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
            `}</style>
        </div>
    );
};

export default UserWalletList;